// format.js - Turning data into the strings a person reads. Every price, weight,
// date, and phone number on the site and in the dashboard goes through here, so
// they are formatted identically everywhere.
//
// LEGEND
//   1. money / moneyExact   currency
//   2. grams / ounces / dwt weights and unit conversion
//   3. day / docDate / ago  dates
//   4. phone / initials     people
//   5. titleCase / plural   small text helpers
//   6. METAL_LABELS         the shared vocabulary

////////// 1. MONEY //////////

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const usdExact = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const money = (value) => (Number.isFinite(Number(value)) ? usd.format(Number(value)) : "—");
export const moneyExact = (value) => (Number.isFinite(Number(value)) ? usdExact.format(Number(value)) : "—");

// A price somebody is quoted, as they would say it: "$2" for two dollars and
// "$2.50" for two fifty. `money` rounds to the dollar, which is right for a
// payout and wrong for a price, because a restock set to $2.50 printed as "$3"
// is a price we did not charge. `moneyExact` is right but reads as a receipt:
// "$2.00" on a page that is selling something looks like small print.
export const price = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return Number.isInteger(amount) ? usd.format(amount) : usdExact.format(amount);
};

// A percentage we state: "5%", "7.5%". Never "7.50%" and never rounded, for the
// same reason as `price`: a rate set at 7.5 and printed as 8 is a rate we do
// not pay.
export const percent = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return `${Number.isInteger(amount) ? amount : Number(amount.toFixed(2))}%`;
};

////////// 2. WEIGHT //////////

export const GRAMS_PER_OZT = 31.1034768;
export const GRAMS_PER_DWT = 1.55517384;

export const UNITS = [
  { value: "g", label: "grams", short: "g", perGram: 1 },
  { value: "dwt", label: "pennyweight", short: "dwt", perGram: GRAMS_PER_DWT },
  { value: "ozt", label: "troy ounces", short: "ozt", perGram: GRAMS_PER_OZT },
  { value: "lb", label: "pounds", short: "lb", perGram: 453.59237 },
];

export const toGrams = (weight, unit = "g") =>
  (Number(weight) || 0) * (UNITS.find((u) => u.value === unit)?.perGram ?? 1);

export const fromGrams = (grams, unit = "g") =>
  (Number(grams) || 0) / (UNITS.find((u) => u.value === unit)?.perGram ?? 1);

export const grams = (value, decimals = 2) =>
  Number.isFinite(Number(value)) ? `${Number(value).toFixed(decimals)} g` : "—";

export const ounces = (gramValue, decimals = 3) =>
  Number.isFinite(Number(gramValue)) ? `${(Number(gramValue) / GRAMS_PER_OZT).toFixed(decimals)} ozt` : "—";

export const perGram = (value) =>
  Number.isFinite(Number(value)) ? `$${Number(value).toFixed(2)}/g` : "—";

////////// 3. DATES //////////

const asDate = (value) => (value ? new Date(value) : null);

export const day = (value) => {
  const date = asDate(value);
  if (!date || Number.isNaN(+date)) return "—";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric" });
};

// A published date: a legal document coming into force, an article going up.
// Always carries its year, because "in force from 31 August" is not a date.
//
// A bare "2026-08-31" parses as UTC midnight, which renders as the 30th
// everywhere in the United States. A legal effective date printed a day early is
// exactly the kind of small wrong number that costs an argument, so a plain
// date string is read as a local calendar date rather than an instant.
export const docDate = (value) => {
  const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  const date = plain
    ? new Date(Number(plain[1]), Number(plain[2]) - 1, Number(plain[3]))
    : asDate(value);
  if (!date || Number.isNaN(+date)) return "—";
  return date.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });
};

export const dayLong = (value) => {
  const date = asDate(value);
  if (!date || Number.isNaN(+date)) return "—";
  return date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
};

export const dayTime = (value) => {
  const date = asDate(value);
  if (!date || Number.isNaN(+date)) return "—";
  return `${day(value)}, ${date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
};

export const time = (value) => {
  const date = asDate(value);
  if (!date || Number.isNaN(+date)) return "";
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

// "3 minutes ago", "yesterday", "12 Mar". Anything older than a week gets a date,
// because "47 days ago" is not information anybody can use.
export function ago(value) {
  const date = asDate(value);
  if (!date || Number.isNaN(+date)) return "";
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  if (seconds < 45) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return day(value);
}

////////// 4. PEOPLE //////////

export function phone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (ten.length !== 10) return value || "";
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

export const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() || "").join("") || "?";

////////// 5. TEXT //////////

export const titleCase = (value = "") =>
  String(value).replace(/[_-]+/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());

export const plural = (count, one, many) => `${count} ${count === 1 ? one : many || `${one}s`}`;

export const truncate = (value = "", max = 120) =>
  value.length > max ? `${value.slice(0, max - 1).trimEnd()}…` : value;

////////// 6. VOCABULARY //////////

// A mailing address as somebody would type it onto an envelope, or paste into
// the address box on stamps.com. Newlines, not commas: it is going into a form
// that wants lines, and a person reading it wants to see the shape of an address
// rather than one long string.
export function addressLines(customer = {}) {
  const address = customer.address || {};
  const name = [customer.firstName, customer.lastName].filter(Boolean).join(" ").trim();
  return [
    name,
    address.line1,
    address.line2,
    [address.city, address.state].filter(Boolean).join(", ") + (address.postal ? ` ${address.postal}` : ""),
    address.country && address.country !== "US" ? address.country : null,
  ].map((line) => String(line || "").trim()).filter(Boolean).join("\n");
}

export const METAL_LABELS = {
  gold: "Gold",
  silver: "Silver",
  platinum: "Platinum",
  palladium: "Palladium",
  other: "Other",
};

export const PAYOUT_LABELS = {
  ach: "Bank transfer (ACH)",
  check: "Paper check",
  zelle: "Zelle",
  cashapp: "Cash App",
  paypal: "PayPal",
  wire: "Wire transfer",
  cash: "Cash",
};

// Where somebody says they found us. The keys are HEARD_SOURCES in
// server/src/models/Order.js and a smoke check fails if the two lists drift.
//
// Insertion order is the order the tiles are shown in, so the two channels we
// buy on lead and the ones a person is proudest to say come after. These are
// written the way a customer would say them out loud, not the way a report
// would name them: nobody has ever thought of themselves as "paid social".
export const HEARD_LABELS = {
  google_search: "Google search",
  google_ad: "A Google ad",
  facebook_post: "Facebook, a post or a group",
  facebook_ad: "A Facebook ad",
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube: "YouTube",
  referral: "Someone told me",
  our_mail: "A mailer we sent you",
  in_person: "A gold show, or in person",
  other: "Somewhere else",
};

export const TEST_LABELS = {
  acid: "Acid test",
  xrf: "XRF analyser",
  scratch: "Scratch test",
  stamp: "Hallmark",
  specific_gravity: "Specific gravity",
  other: "Other",
};

export const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];
